import { useCallback } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import { type SurfaceType } from "../../../gql-generated/types";
import { SearchParamKeys, useSearchParams } from "../../../shared/hooks";
import { AbsoluteRoute } from "../absoluteRoutes";

export interface LensSubmissionRoutesV3 {
    goToLensCreate: (submissionId: string, lensFolderId: string, surfaceType: readonly SurfaceType[]) => void;
    goToLensSelect: (submissionId: string, lensFolder?: string, surfaceType?: readonly SurfaceType[]) => void;
    goToLensCreateSuccess: (submissionId: string, lensId: string) => void;
    goToFlowRedirector: (submissionId: string) => void;
    goToLensFolderSelect: (submissioId: string) => void;
}

export const useLensSubmissionRoutesV3 = (): LensSubmissionRoutesV3 => {
    const navigate = useNavigate();

    const searchParams = useSearchParams();

    const goToLensCreate = useCallback(
        (submissionId: string, lensFolderId?: string, surfaceType?: readonly SurfaceType[]) => {
            const urlSearchParams = new URLSearchParams(searchParams);

            if (lensFolderId) {
                urlSearchParams.set(SearchParamKeys.LensFolder, lensFolderId);
            }

            if (surfaceType && surfaceType.length > 0) {
                urlSearchParams.set(SearchParamKeys.SurfaceType, surfaceType.join(","));
            }

            navigate({
                pathname: generatePath(AbsoluteRoute.SUBMIT_LENS_CREATE, {
                    submissionId,
                }),
                search: `?${urlSearchParams.toString()}`,
            });
        },
        [navigate, searchParams],
    );

    const goToLensSelect = useCallback(
        (submissionId: string, lensFolderId?: string, surfaceType?: readonly SurfaceType[]) => {
            const urlSearchParams = new URLSearchParams(searchParams);

            if (lensFolderId) {
                urlSearchParams.set(SearchParamKeys.LensFolder, lensFolderId);
            }

            if (surfaceType && surfaceType.length > 0) {
                urlSearchParams.set(SearchParamKeys.SurfaceType, surfaceType.join(","));
            }

            navigate({
                pathname: generatePath(AbsoluteRoute.SUBMIT_LENS_SELECT, {
                    submissionId,
                }),
                search: `?${urlSearchParams.toString()}`,
            });
        },
        [navigate, searchParams],
    );

    const goToLensCreateSuccess = useCallback(
        (submissionId: string, lensId: string) => {
            navigate({
                pathname: generatePath(AbsoluteRoute.SUBMIT_LENS_CREATE_SUCCESS, {
                    submissionId,
                    lensId,
                }),
                search: `?${searchParams.toString()}`,
            });
        },
        [navigate, searchParams],
    );

    const goToFlowRedirector = useCallback(
        (submissionId: string) => {
            navigate({
                pathname: generatePath(AbsoluteRoute.SUBMIT_FLOW_REDIRECTOR, {
                    submissionId,
                }),
                search: `?${searchParams.toString()}`,
            });
        },
        [navigate, searchParams],
    );

    const goToLensFolderSelect = useCallback(
        (submissionId: string) => {
            navigate({
                pathname: generatePath(AbsoluteRoute.SUBMIT_LENS_FOLDER_SELECT, {
                    submissionId,
                }),
                search: `?${searchParams.toString()}`,
            });
        },
        [navigate, searchParams],
    );

    return {
        goToLensCreate,
        goToLensSelect,
        goToLensCreateSuccess,
        goToFlowRedirector,
        goToLensFolderSelect,
    };
};
